import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'semantic-ui-react'
import useAxios from 'hooks/useAxios'
import toast from 'libs/toast'
import CustomRoleForm from 'components/CustomRoleForm'
import constants from 'config/constants'
import useUser from 'hooks/useUser'
import * as R from 'ramda'
import useSaveUserActivityLog, { ACCOUNT_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import { validateRequiredText } from 'libs/form-validation'

const CreateOrUpdateRoleModal = ({
  close,
  editingRole,
  isOpenRoleModal,
  modal: Modal,
  roles,
  setEditingRole,
  setEditingRoleList,
  isFetchingSurveys,
  isFetchingDashboards,
  hasManageRolePermission,
  loading,
}) => {
  const hiddenRefButton = useRef(null)
  const saveUserActivityLog = useSaveUserActivityLog()
  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)
  const [nameError, setNameError] = useState('')

  useEffect(() => {
    if (!isOpenRoleModal) {
      setNameError('')
    }
  }, [isOpenRoleModal])

  useEffect(() => {
    if (nameError && editingRole.name && editingRole.name.trim()) {
      setNameError('')
    }
  }, [editingRole.name, nameError])

  const { fetch: createRole, loading: isCreatingRole } = useAxios({
    method: 'post',
    url: 'account/roles/',
    skip: true,
  })

  const { fetch: updateRole, loading: isUpdatingRole } = useAxios({
    method: 'patch',
    url: `account/roles/${editingRole.id}/`,
    skip: true,
  })

  function handleSubmit() {
    const error = validateRequiredText('Role name', editingRole.name || '')
    if (error) {
      setNameError(error)
      return
    }

    if (editingRole.id) {
      handleUpdateRole()
    } else {
      handleCreateRole()
    }
  }

  function _getPermissions(permissionAccesses) {
    return permissionAccesses.filter((item) => item.has_permission).map((item) => item.token)
  }

  function handleCreateRole() {
    const existedRoles = [...roles]
    const isDuplicate = existedRoles.some(
      (role) => role.name.toLowerCase() === editingRole.name.toLowerCase()
    )

    if (isDuplicate) {
      toast.error('The role you are trying to create already exists.')
      return
    }

    const data = {
      ...editingRole,
      survey_access: _getPermissions(editingRole.survey_access),
      dashboard_access: _getPermissions(editingRole.dashboard_access),
    }

    createRole({ data })
      .then((res) => {
        if (res.data) {
          toast.success('New role created!')
          setEditingRoleList((prev) => {
            return [...prev, { ...res.data }]
          })
          close()
          saveUserActivityLog({
            action: ACCOUNT_ACTION_NAME.created_role,
            statusCode: res.status,
            requestBody: data,
            target: `com_${companyId}`,
          })
        }
      })
      .catch((err) => {
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        saveUserActivityLog({
          action: ACCOUNT_ACTION_NAME.created_role,
          statusCode: err.response.status,
          requestBody: data,
          errorResponseBody: err.response.data,
          target: `com_${companyId}`,
        })
        console.error(err)
      })
  }

  function handleUpdateRole() {
    const existedRoles = [...roles]
    const isNameChanged = editingRole.name === editingRole.original_name
    const isDuplicate = existedRoles.some(
      (role) => role.name.toLowerCase() === editingRole.name.toLowerCase()
    )
    const isDefaultCollaborator = editingRole.name === 'collaborator'

    if (!isNameChanged && isDuplicate) {
      toast.error('The role you are trying to update already exists.')

      return
    }
    const data = isDefaultCollaborator
      ? {
          id: editingRole.id,
          name: editingRole.name,
          permissions: {
            view_feedback: editingRole.permissions.view_feedback,
          },
          survey_access: [],
          dashboard_access: [],
        }
      : {
          ...editingRole,
          survey_access: _getPermissions(editingRole.survey_access),
          dashboard_access: _getPermissions(editingRole.dashboard_access),
        }

    updateRole({ data })
      .then((res) => {
        if (res.data) {
          toast.success('Role updated!')
          setEditingRoleList((prev) => {
            const filteredResults = prev.map((role) =>
              role.id === editingRole.id ? { ...res.data } : role
            )

            return filteredResults
          })
          close()
        }
      })
      .catch((err) => {
        if (err.response.status === 403) {
          toast.error(constants.permissionDeniedErrorMessage)
        } else {
          toast.error(
            'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
          )
        }
        console.error(err)
      })
  }

  const isDefaultRole = editingRole.is_predefined
  const isDefaultCollaborator = editingRole.name === 'collaborator'

  const isHideSaveButton =
    !hasManageRolePermission || ['owner', 'admin', 'manager', 'billing'].includes(editingRole.name)

  return (
    <Modal open={isOpenRoleModal} onClose={close} size="small">
      <Modal.Header>{editingRole.id ? 'Edit role' : 'Create new role'}</Modal.Header>
      <Modal.Content className="ui segment basic mt0">
        <CustomRoleForm
          onChange={setEditingRole}
          state={editingRole}
          hiddenRefButton={hiddenRefButton}
          onSubmit={handleSubmit}
          isFetchingSurveys={isFetchingSurveys}
          isFetchingDashboards={isFetchingDashboards}
          isDefaultRole={isDefaultRole}
          isDisabled={!hasManageRolePermission || isDefaultRole}
          isDefaultCollaborator={isDefaultCollaborator}
          loading={loading}
          nameError={nameError}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={close}>Cancel</Button>
        {hasManageRolePermission && !isHideSaveButton && (
          <Button
            onClick={() => hiddenRefButton.current.click()}
            disabled={isCreatingRole || isUpdatingRole || loading}
            loading={isCreatingRole || isUpdatingRole}
            primary
          >
            {editingRole.id ? 'Save' : 'Create role'}
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  )
}

export default CreateOrUpdateRoleModal



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Admin/Roles/RoleModal.js